/** @jsxImportSource @emotion/react */
import { SerializedStyles } from '@emotion/react'
import { Box, Skeleton, Typography, useTheme } from '@mui/material'
import React, { createContext, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  buttonCss,
  fieldWrap,
  innerPadding,
  inputCss,
  linkStyle,
  navbarContentStyles,
  navbarIndicatorStyles,
  navbarLinkStyles,
  navbarLogoStyles,
  navbarSubStyles,
  NavbarTablist,
  navbarTriggerStyles,
  navbarViewportStyles,
  navbarWrapper,
  navbarWrapperStyles,
  root,
} from './Navbar.style'
import { isAuthenticatedAtom } from '@app/state/authAtom'
import { useAtomValue } from 'jotai'

interface NavbarContextValue {
  activeItem?: string
  setActiveItem: (item: string) => void
}

const NavbarContext = createContext<NavbarContextValue | null>(null)

const NavbarWrapper: React.FC<NavbarWrapperProps> = ({ children, className, style }) => {
  const theme = useTheme()
  const [activeItem, setActiveItem] = useState<string>()

  const contextValue: NavbarContextValue = {
    activeItem,
    setActiveItem,
  }

  return (
    <NavbarContext.Provider value={contextValue}>
      <Box
        component="nav"
        css={navbarWrapperStyles(theme)}
        className={className}
        style={style}
        role="navigation"
      >
        {children}
      </Box>
    </NavbarContext.Provider>
  )
}

const NavbarLogo: React.FC<NavbarLogoProps> = ({
  children,
  onClick,
  className,
  css,
  'aria-label': ariaLabel = 'Navigate to home',
}) => {
  const theme = useTheme()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  return (
    <Box
      component="div"
      css={[navbarLogoStyles(theme), css]}
      className={className}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {isAuthenticated ? children : null}
    </Box>
  )
}

const NavbarList: React.FC<NavbarListProps> = ({ children, className }) => {
  const theme = useTheme()
  return (
    <Box className={className} css={navbarWrapper(theme)}>
      <Box role="tablist" css={NavbarTablist}>
        {children}
      </Box>
    </Box>
  )
}

const NavbarTrigger: React.FC<NavbarTriggerProps> = ({
  children,
  asChild = false,
  className,
  onClick,
}) => {
  const theme = useTheme()

  const handleClick = () => {
    onClick?.()
  }

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, {
      css: navbarTriggerStyles(theme),
      className,
      onClick: handleClick,
    })
  }

  return (
    <Box
      component="button"
      css={navbarTriggerStyles(theme)}
      className={className}
      onClick={handleClick}
      role="menuitem"
      aria-haspopup="true"
    >
      {children}
    </Box>
  )
}

const NavbarLink: React.FC<NavbarLinkProps> = ({
  children,
  href,
  onClick,
  asChild = false,
  className,
  isActive = false,
}) => {
  const theme = useTheme()

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault()
      onClick()
    }
  }

  if (asChild) {
    return React.cloneElement(children as React.ReactElement, {
      css: navbarLinkStyles(theme, isActive),
      className,
      onClick: handleClick,
      href,
    })
  }

  return (
    <Box
      component={href ? 'a' : 'button'}
      href={href}
      css={navbarLinkStyles(theme, isActive)}
      className={className}
      onClick={handleClick}
      role="menuitem"
    >
      {children}
    </Box>
  )
}

const NavbarContent: React.FC<NavbarContentProps> = ({ children, className }) => {
  const theme = useTheme()
  const [isOpen] = useState(false)

  return (
    <Box
      css={navbarContentStyles(theme)}
      className={className}
      data-state={isOpen ? 'open' : 'closed'}
      role="menu"
    >
      {children}
    </Box>
  )
}

const NavbarSub: React.FC<NavbarSubProps> = ({ children, className }) => {
  return (
    <Box css={navbarSubStyles} className={className}>
      {children}
    </Box>
  )
}

const NavbarViewport: React.FC<NavbarViewportProps> = ({ children, className }) => {
  return (
    <Box css={navbarViewportStyles} className={className}>
      {children}
    </Box>
  )
}

const NavbarIndicator: React.FC<NavbarIndicatorProps> = ({ className }) => {
  const theme = useTheme()

  return <Box css={navbarIndicatorStyles(theme)} className={className} aria-hidden="true" />
}

const NavbarItem = ({ label, path }: { label: string; path: string }) => {
  const location = useLocation()
  const currentSubTopic = location.pathname.split('/')[3]
  const pathSection = path.split('/')[2]
  const isActive = currentSubTopic === pathSection

  return (
    <NavLink to={path} role="tab" className={isActive ? 'active' : undefined} css={linkStyle}>
      <span css={innerPadding}>{label}</span>
    </NavLink>
  )
}

export function NavbarSkeleton({
  navCount = 6,
  searchWidth = 420,
  searchHeight = 44,
  isError,
}: NavbarSkeletonProps) {
  const theme = useTheme()

  const widths = [68, 92, 84, 110, 76, 96, 72, 88]

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        display: 'flex',
        height: '70px',
        py: 0,
        pr: '20px',
        pl: '24px',
        alignItems: 'center',
        flexShrink: 0,
        borderBottom: '1px solid #d6d6d6',
        backgroundColor: theme.palette.common.white,
        backdropFilter: 'saturate(180%) blur(6px)',
      }}
    >
      {/* Left: search */}
      <Box
        sx={{
          width: `${searchWidth}px`,
          height: `${searchHeight}px`,
          display: 'flex',
          alignItems: 'center',
          borderRadius: '8px',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Skeleton variant="rectangular" width={300} height={40} />
      </Box>

      <Box
        role="tablist"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          columnGap: '32px',
          px: 3,
          minWidth: 0,
        }}
      >
        {!isError ? (
          Array.from({ length: navCount }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              sx={{ borderRadius: '999px', flexShrink: 0 }}
              width={widths[i % widths.length]}
              height={32}
            />
          ))
        ) : (
          <Typography variant="body2">Something went wrong.</Typography>
        )}
      </Box>

      {/* Right: logo */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', ml: 2 }}>
        <Skeleton variant="rectangular" width={300} height={40} />
      </Box>
    </Box>
  )
}

type SearchProps = {
  value?: string
  defaultValue?: string
  placeholder?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  className?: string
  disabled?: boolean
  'aria-label'?: string
  visible?: boolean
}

export const NavbarSearch: React.FC<SearchProps> = ({
  value,
  defaultValue,
  placeholder = 'Search…',
  onChange,
  onSubmit,
  className,
  disabled,
  visible = false,
  'aria-label': ariaLabel = 'GuideSearch',
}) => {
  const theme = useTheme()
  const [internal, setInternal] = React.useState(defaultValue ?? '')
  const isControlled = value !== undefined
  const currentValue = isControlled ? (value as string) : internal
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (!isControlled) setInternal(v)
    onChange?.(v)
  }

  const submit = () => {
    onSubmit?.(currentValue)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div className={className} css={root(visible, theme)}>
      <div css={fieldWrap} data-disabled={disabled || undefined}>
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          css={inputCss}
          type="search"
          value={currentValue}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          css={buttonCss}
          onClick={submit}
          disabled={disabled}
          aria-label="Submit search"
        >
          <SearchIcon />
        </button>
      </div>
    </div>
  )
}

const SearchIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const Navbar = {
  Wrapper: NavbarWrapper,
  Logo: NavbarLogo,
  List: NavbarList,
  Item: NavbarItem,
  Trigger: NavbarTrigger,
  Link: NavbarLink,
  Content: NavbarContent,
  Sub: NavbarSub,
  Viewport: NavbarViewport,
  Indicator: NavbarIndicator,
  Search: NavbarSearch,
  Skeleton: NavbarSkeleton,
}

export default Navbar

interface NavbarWrapperProps {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

interface NavbarLogoProps {
  children?: React.ReactNode
  onClick?: () => void
  className?: string
  css?: SerializedStyles | SerializedStyles[]
  'aria-label'?: string
}

interface NavbarListProps {
  children: React.ReactNode
  className?: string
}

interface NavbarTriggerProps {
  children: React.ReactNode
  asChild?: boolean
  className?: string
  onClick?: () => void
}

interface NavbarLinkProps {
  children: React.ReactNode
  href?: string
  onClick?: () => void
  asChild?: boolean
  className?: string
  isActive?: boolean
}

interface NavbarContentProps {
  children: React.ReactNode
  className?: string
}

interface NavbarSubProps {
  children: React.ReactNode
  className?: string
}

interface NavbarViewportProps {
  children?: React.ReactNode
  className?: string
}

interface NavbarIndicatorProps {
  className?: string
}

type NavbarSkeletonProps = {
  navCount?: number
  searchWidth?: number
  searchHeight?: number
  isError?: boolean
}
